import { useCallback, useEffect, useState } from 'react';
import { apiClient } from '@/lib/apiClient';
import { AlertTriangle, ChevronDown, ChevronUp, Database, Users } from 'lucide-react';

interface DataQualityIssue {
  type: string;
  description: string;
  count: number;
  entityIds: string[];
}

interface UserAdoptionRow {
  userId: string;
  email: string;
  role: string;
  lastActivityDate: string | null;
  dealsUpdatedThisWeek: number;
  tasksCompletedThisWeek: number;
  status: string;
}

const SEVERITY: Record<string, { label: string; className: string }> = {
  OPPORTUNITY_NO_CONTACT: { label: 'High', className: 'bg-red-900/40 text-red-200' },
  ACCOUNT_NO_CONTACTS: { label: 'Medium', className: 'bg-amber-900/40 text-amber-200' },
  LEAD_NO_EMAIL: { label: 'Medium', className: 'bg-amber-900/40 text-amber-200' },
  STALE_LEADS: { label: 'Low', className: 'bg-blue-900/40 text-blue-200' },
};

export function DataQualityPage() {
  const [issues, setIssues] = useState<DataQualityIssue[]>([]);
  const [adoption, setAdoption] = useState<UserAdoptionRow[]>([]);
  const [expandedIssue, setExpandedIssue] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [qualityRes, adoptionRes] = await Promise.all([
        apiClient.get<DataQualityIssue[]>('/analytics/data-quality'),
        apiClient.get<UserAdoptionRow[]>('/analytics/adoption'),
      ]);
      setIssues(qualityRes.data);
      setAdoption(adoptionRes.data);
    } catch {
      setError('Failed to load data quality metrics');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  function toggleExpand(type: string) {
    setExpandedIssue((prev) => (prev === type ? null : type));
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12 text-muted-foreground">
        Loading...
      </div>
    );
  }

  return (
    <div className="space-y-8">
      <h1 className="text-2xl font-semibold">Data Quality &amp; Adoption</h1>

      {error && <p className="text-sm text-destructive">{error}</p>}

      {/* Data Quality Section */}
      <section className="space-y-4">
        <div className="flex items-center gap-2">
          <Database className="h-5 w-5 text-muted-foreground" />
          <h2 className="text-lg font-medium">Data Quality</h2>
        </div>

        {issues.length === 0 && (
          <p className="text-sm text-muted-foreground">No data quality issues found.</p>
        )}

        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          {issues.map((issue) => {
            const severity = SEVERITY[issue.type] ?? {
              label: 'Info',
              className: 'bg-muted text-foreground',
            };
            const isExpanded = expandedIssue === issue.type;

            return (
              <div key={issue.type} className="rounded-lg border bg-card p-4 space-y-2">
                <div className="flex items-start justify-between">
                  <div className="flex items-center gap-2">
                    <AlertTriangle className="h-4 w-4 text-muted-foreground" />
                    <span
                      className={`rounded-full px-2 py-0.5 text-[10px] font-medium ${severity.className}`}
                    >
                      {severity.label}
                    </span>
                  </div>
                  <span className="text-2xl font-bold">{issue.count}</span>
                </div>
                <p className="text-sm text-muted-foreground">{issue.description}</p>
                {issue.entityIds.length > 0 && (
                  <div>
                    <button
                      onClick={() => toggleExpand(issue.type)}
                      className="flex items-center gap-1 text-xs text-primary hover:underline"
                    >
                      {isExpanded ? (
                        <ChevronUp className="h-3 w-3" />
                      ) : (
                        <ChevronDown className="h-3 w-3" />
                      )}
                      View {issue.entityIds.length} affected
                    </button>
                    {isExpanded && (
                      <ul className="mt-1 space-y-0.5 text-xs text-muted-foreground">
                        {issue.entityIds.map((id) => (
                          <li key={id} className="truncate font-mono">
                            {id.slice(0, 8)}...
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </section>

      {/* Adoption Report Section */}
      <section className="space-y-4">
        <div className="flex items-center gap-2">
          <Users className="h-5 w-5 text-muted-foreground" />
          <h2 className="text-lg font-medium">Adoption Report</h2>
        </div>

        <div className="overflow-hidden rounded-lg border bg-card">
          <table className="w-full text-sm">
            <thead className="bg-muted text-left text-xs uppercase text-muted-foreground">
              <tr>
                <th className="px-3 py-2">Email</th>
                <th className="px-3 py-2">Role</th>
                <th className="px-3 py-2">Last Activity</th>
                <th className="px-3 py-2">Deals Updated</th>
                <th className="px-3 py-2">Tasks Completed</th>
                <th className="px-3 py-2">Status</th>
              </tr>
            </thead>
            <tbody>
              {adoption.map((row) => (
                <tr key={row.userId} className="border-t">
                  <td className="px-3 py-2">{row.email}</td>
                  <td className="px-3 py-2">{row.role}</td>
                  <td className="px-3 py-2">{row.lastActivityDate ?? '---'}</td>
                  <td className="px-3 py-2 text-center">{row.dealsUpdatedThisWeek}</td>
                  <td className="px-3 py-2 text-center">{row.tasksCompletedThisWeek}</td>
                  <td className="px-3 py-2">
                    <span
                      className={`rounded-full px-2 py-0.5 text-[10px] font-medium ${
                        row.status === 'ACTIVE'
                          ? 'bg-emerald-900/40 text-emerald-200'
                          : 'bg-red-900/40 text-red-200'
                      }`}
                    >
                      {row.status}
                    </span>
                  </td>
                </tr>
              ))}
              {adoption.length === 0 && (
                <tr>
                  <td colSpan={6} className="px-3 py-6 text-center text-muted-foreground">
                    No adoption data available
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </section>
    </div>
  );
}
